import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useAuth } from "../../contexts/AuthContext";
import { apiGet, apiPost } from "../../utils/api";
import SubmitButton from "../SubmitButton";
import { AuthError, OAuthProvider } from "../../shared/types";
import { isInZoomClient, triggerInClientAuth } from "../../utils/zoomHelper";

type Message = {
  type: 'success' | 'error';
  text: string;
}

interface LoginProps {
  mode: 'admin' | 'patient';
  orgId: string;
  passwordOnly?: boolean;
  externalPasswordHandler?: (email: string, password: string) => Promise<void>;
  redirectUrl?: string;
}

const Login = ({ mode, orgId, passwordOnly, externalPasswordHandler, redirectUrl }: LoginProps) => {
  const { login } = useAuth();
  const [ loginEmail, setLoginEmail ] = useState("");
  const [ password, setPassword ] = useState("");
  const [ loading, setLoading ] = useState(false);
  const [ providersLoading, setProvidersLoading ] = useState(!passwordOnly);
  const [ inZoomClient, setInZoomClient ] = useState<boolean | null>(null);
  const [ oauthProviders, setOauthProviders ] = useState<Record<OAuthProvider, boolean> | null>(
    passwordOnly ? {
      google: false,
      microsoft: false,
      epic: false,
      webex: false,
      zoom: false
    } : null
  );
  const visibleProviders = oauthProviders && inZoomClient
    ? { google: false, microsoft: false, epic: false, webex: false, zoom: oauthProviders.zoom }
    : oauthProviders;
  const hasOAuthOptions = visibleProviders && Object.values(visibleProviders).some(Boolean);

  const [ message, setMessage ] = useState<Message | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (error) {
      const errorMessages: Record<AuthError, string> = {
        [AuthError.OAUTH_FAILED]: 'OAuth authentication failed. Please try again.',
        [AuthError.NO_EMAIL]: 'Unable to retrieve email from OAuth provider.',
        [AuthError.OAUTH_ERROR]: 'An error occurred during OAuth authentication.',
        [AuthError.USER_NOT_FOUND]: 'User not found in Organization.',
        [AuthError.OAUTH_NOT_CONFIGURED]: 'OAuth provider not configured for this organization.',
        [AuthError.SESSION_EXPIRED]: 'Your session has expired.',
        [AuthError.INCORRECT_PERMISSIONS]: 'You have incorrect permissions.',
      };
      const errorMessage = errorMessages[error as AuthError];
      if (!errorMessage) {
        return null;
      }
      return { type: 'error', text: errorMessage };
    }
    return null;
  });

  useEffect(() => {
    isInZoomClient().then((ctx) => setInZoomClient(!!ctx));
  }, []);

  useEffect(() => {
    if (passwordOnly) return;

    const fetchProviders = async () => {
      try {
        const params = new URLSearchParams({ type: mode });

        const response = await apiGet(`/api/${orgId}/auth/providers?${params.toString()}`);
        const data = await response.json();

        if (response.ok) {
          setOauthProviders(data);
        } else {
          console.error('Failed to fetch providers:', data);
          setOauthProviders({ google: false, microsoft: false, epic: false, webex: false, zoom: false });
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
        setOauthProviders({ google: false, microsoft: false, epic: false, webex: false, zoom: false });
      } finally {
        setProvidersLoading(false);
      }
    };

    fetchProviders();
  }, [mode, orgId, passwordOnly]);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const success = await login(loginEmail, password, orgId);

      if (success) {
        let redirectUrl = '/organizations';
        if (mode === 'admin') {
          redirectUrl = `/${orgId}/general-settings`;
        } else if (mode === 'patient') {
          redirectUrl = `/${orgId}/patient_dashboard`;
        }

        window.location.href = redirectUrl;
      } else {
        setMessage({ type: 'error', text: 'Login failed. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: (error as Error).message || 'Login failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthClick = async (provider: OAuthProvider) => {
    setMessage(null);
    const loginType = mode === 'admin' ? 'admin' : 'patient';

    if (provider === 'epic') {
      const epicUrl = `/api/epic_auth?org_id=${encodeURIComponent(orgId)}&type=${loginType}`;
      window.location.href = epicUrl;
      return;
    }

    // ZOOM DESKTOP CLIENT
    if (provider === 'zoom' && await isInZoomClient()) {
      try {
        await triggerInClientAuth(orgId, mode);
      } catch (error) {
        setMessage({ type: 'error', text: (error as Error).message || 'Unable to sign in with Zoom. Please try again.' });
      }
      return;
    }

    try {
      const response = await apiPost(`/api/${orgId}/oauth/start`, {
        provider,
        type: mode,
        redirect_url: redirectUrl,
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: data.message || 'Unable to start sign-in.' });
        return;
      }
      if (data.redirectUrl) window.location.href = data.redirectUrl;
    } catch {
      setMessage({ type: 'error', text: 'Unable to start sign-in. Please try again.' });
    }
  };

  if (providersLoading || inZoomClient === null) {
    return <div className="text-center text-gray-600">Loading login options...</div>;
  }

  return (
    <>
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
          <button className="ml-auto" onClick={() => setMessage(null)}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="space-y-4">
        {mode === "admin" &&
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
              placeholder="you@example.com"
              required
            />
          </div>
        }

        {mode === "admin" && !inZoomClient && (
          <>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
                placeholder="••••••••"
                required={!hasOAuthOptions}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    if (externalPasswordHandler) {
                      setLoading(true);
                      await externalPasswordHandler(loginEmail, password);
                      setLoading(false);
                    } else {
                      handlePasswordLogin(e);
                    }
                  }
                }}
              />
            </div>
            <SubmitButton
              loading={loading}
              text="Sign in with Password"
              loadingText="Signing in..."
              onClick={async (e) => {
                if (externalPasswordHandler) {
                  setLoading(true);
                  await externalPasswordHandler(loginEmail, password);
                  setLoading(false);
                } else {
                  handlePasswordLogin(e);
                }
              }}
              className="w-full"
              disabled={!loginEmail.trim() || !password.trim()}
            />

            {hasOAuthOptions && (
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
            )}
          </>
        )}

        {hasOAuthOptions && visibleProviders && (
          <div className="flex flex-wrap justify-center gap-2">
            {visibleProviders.google && (
              <button
                type="button"
                onClick={() => handleOAuthClick('google')}
                className="min-w-[80px] flex-1 inline-flex flex-col justify-center items-center py-4 px-2 border border-gray-300 rounded-md shadow-sm bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6 mb-1.5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Google
              </button>
            )}

            {visibleProviders.microsoft && (
              <button
                type="button"
                onClick={() => handleOAuthClick('microsoft')}
                className="min-w-[80px] flex-1 inline-flex flex-col justify-center items-center py-4 px-2 border border-gray-300 rounded-md shadow-sm bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6 mb-1.5" viewBox="0 0 23 23" fill="none">
                  <path fill="#F25022" d="M0 0h11v11H0z" />
                  <path fill="#00A4EF" d="M12 0h11v11H12z" />
                  <path fill="#7FBA00" d="M0 12h11v11H0z" />
                  <path fill="#FFB900" d="M12 12h11v11H12z" />
                </svg>
                Microsoft
              </button>
            )}

            {visibleProviders.epic && (
              <button
                type="button"
                onClick={() => handleOAuthClick('epic')}
                className="min-w-[80px] flex-1 inline-flex flex-col justify-center items-center py-4 px-2 border border-gray-300 rounded-md shadow-sm bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="h-4 mb-1.5" viewBox="0 0 247.82915 96.914589" xmlns="http://www.w3.org/2000/svg">
                  <g transform="translate(27.908633,-85.347986)">
                    <path fill="#ba0239" d="m 41.24081,179.05316 c -0.09005,-0.14571 -0.04364,-0.89124 0.103156,-1.65674 0.432927,-2.25768 2.091312,-10.10636 2.802464,-13.26328 0.362143,-1.60762 0.564581,-3.0168 0.449866,-3.13151 -0.114718,-0.11472 -15.800256,-0.23879 -34.8567541,-0.27571 l -34.6481749,-0.0671 0.0136,-1.32291 c 0.0075,-0.72761 0.631569,-4.29948 1.386865,-7.9375 2.881788,-13.88066 3.517978,-16.96972 3.732612,-18.12396 0.121771,-0.65485 0.831056,-4.16719 1.57619,-7.80521 2.133817,-10.41809 5.009733,-24.50041 5.886623,-28.824658 0.440976,-2.174602 0.916879,-4.168899 1.057564,-4.43177 0.244675,-0.457181 1.7779892,-0.477948 35.288158,-0.477948 24.316974,0 35.085206,0.08549 35.205115,0.279513 0.09501,0.153732 -0.0104,1.076467 -0.234225,2.050521 -0.223835,0.974054 -0.843206,3.854601 -1.376381,6.401212 -0.533175,2.54662 -1.080008,5.07606 -1.215184,5.62098 l -0.245776,0.99077 -23.58221,0.0676 -23.5822122,0.0676 -0.1947069,0.66146 c -0.1070874,0.3638 -0.449236,2.09021 -0.7603278,3.83646 -0.3110918,1.74625 -0.7423388,3.98529 -0.9583235,4.97566 -0.2159847,0.99036 -0.3232097,1.9131 -0.2382785,2.05052 0.094726,0.15327 8.0688979,0.24986 20.6283989,0.24986 12.085368,0 20.535239,0.0991 20.623496,0.24192 0.08223,0.13306 -0.264097,2.18689 -0.769623,4.56406 -0.505526,2.37718 -1.11302,5.27464 -1.349986,6.43881 -0.236966,1.16417 -0.535901,2.38456 -0.6643,2.71198 l -0.23345,0.59531 H 24.436159 c -10.455928,0.15392 0,0 -20.7386019,0.10895 l -2.1984385,11.98859 c 23.3691234,0.009 15.2898614,0.031 23.3691234,0.009 l 23.201747,-0.0676 0.33278,-1.5875 c 0.183031,-0.87312 0.708549,-3.3139 1.16782,-5.42396 0.459272,-2.11005 1.232326,-5.74145 1.717895,-8.06979 1.112822,-5.33603 2.950615,-14.01608 3.445179,-16.27187 0.207378,-0.94589 0.642972,-2.94018 0.967989,-4.43177 l 0.590939,-2.71198 9.802514,0.004 c 5.391383,0.002 9.949106,0.0972 10.128271,0.21098 0.251315,0.15964 0.167674,0.91655 -0.365988,3.31208 -0.38046,1.70783 -0.622085,3.17481 -0.53694,3.25995 0.08514,0.0851 0.441426,-0.0677 0.791742,-0.33956 2.753156,-2.13693 3.971433,-2.95961 5.841177,-3.94444 5.919137,-3.11771 13.124641,-4.33609 20.456243,-3.45894 7.31537,0.87521 11.55949,2.68726 15.56232,6.64442 2.85026,2.81773 4.66655,6.4352 5.29786,10.55165 0.0866,0.56466 0.25203,0.92184 0.36763,0.79375 0.19274,-0.21356 0.75962,-2.59919 1.75286,-7.37665 1.36859,-6.58288 2.02343,-9.26364 2.30686,-9.44372 0.44462,-0.28249 19.81482,-0.26107 20.26222,0.0224 0.29455,0.18663 0.3033,0.48522 0.0483,1.64959 -0.17124,0.78195 -0.54707,2.49328 -0.83518,3.80297 -0.2881,1.30968 -0.92268,4.28625 -1.41017,6.61458 -0.48748,2.32833 -0.97102,4.56076 -1.07451,4.96094 -0.33229,1.28479 0.31303,0.79872 1.31261,-0.98868 2.08707,-3.73203 6.19096,-8.07171 10.11238,-10.69339 8.47607,-5.66671 20.96029,-8.36198 34.52773,-7.45433 7.61353,0.50934 11.66123,1.40873 16.27401,3.61606 4.78196,2.28828 7.58826,5.31448 9.16788,9.88626 0.6481,1.87577 0.78594,2.71109 0.8096,4.90647 l 0.0285,2.64584 h -10.69358 -10.69358 l -0.52444,-1.57333 c -1.11437,-3.34313 -4.20307,-5.23538 -9.26279,-5.67472 -6.89706,-0.59888 -12.11234,0.97547 -16.3653,4.94023 -4.82349,4.49664 -7.20114,13.95394 -4.80414,19.10886 1.89192,4.0687 5.90695,6.08542 12.11528,6.08542 6.89511,0 12.12172,-2.52416 15.33919,-7.40798 l 1.22039,-1.85244 h 10.8423 c 9.83705,0 10.84229,0.0399 10.84229,0.43045 0,0.23675 -0.49709,1.33807 -1.10464,2.44739 -6.52912,11.92136 -15.97151,17.86543 -31.39464,19.76324 -4.37749,0.53865 -15.96215,0.40032 -19.76356,-0.23598 -4.77933,-0.8 -7.84239,-1.76532 -11.36945,-3.58307 -3.2901,-1.69564 -6.75877,-5.10161 -8.12087,-7.97411 -0.9772,-2.06078 -1.62921,-4.23755 -1.93141,-6.44807 -0.20993,-1.53558 -0.54978,-1.34795 -0.91331,0.50424 -0.1418,0.72249 -0.67458,3.27815 -1.18395,5.67924 -0.50937,2.4011 -1.11514,5.2586 -1.34614,6.35 -0.23101,1.09141 -0.53802,2.43086 -0.68226,2.97657 l -0.26225,0.99218 h -10.18456 c -8.46436,0 -10.20847,-0.0623 -10.32609,-0.3688 -0.13303,-0.34668 0.4799,-3.65044 1.7478,-9.42078 0.30376,-1.38245 0.6723,-3.16838 0.81898,-3.96875 0.14668,-0.80036 0.3276,-1.66357 0.40206,-1.91823 0.22445,-0.76773 -0.28164,-0.52112 -0.81449,0.39688 -1.80188,3.10433 -5.66768,7.33615 -8.95746,9.80555 -6.85396,5.14479 -14.542548,7.58521 -23.909414,7.58905 -7.943507,0.003 -13.295662,-1.66689 -17.212342,-5.37112 -0.76998,-0.72822 -1.478437,-1.24556 -1.574348,-1.14965 -0.09591,0.0959 -0.332346,0.91811 -0.525412,1.8271 -0.193067,0.90899 -0.763339,3.61724 -1.267267,6.01834 -0.503931,2.40109 -1.162598,5.49672 -1.463704,6.87916 -0.301104,1.38245 -0.738651,3.40651 -0.97232,4.49792 -0.233672,1.09141 -0.502963,2.34156 -0.598424,2.77813 l -0.173564,0.79375 -10.224291,0.0692 c -6.615108,0.0448 -10.28209,-0.0243 -10.388024,-0.19569 z m 48.338839,-31.99413 c 5.976482,-1.72416 9.609791,-5.46398 11.438231,-11.77355 0.89824,-3.09963 0.9404,-6.57785 0.10676,-8.80597 -0.7854,-2.09914 -2.713385,-3.99178 -4.993275,-4.90171 -3.425786,-1.36726 -9.252115,-1.49826 -13.089997,-0.29431 -7.20031,2.25875 -11.752638,8.48681 -11.858929,16.22425 -0.03472,2.52737 0.02839,2.92142 0.699188,4.36563 1.488797,3.20535 4.140438,5.04056 8.249325,5.70939 2.141349,0.34856 7.443872,0.0546 9.448697,-0.52373 z M 129.04388,99.760605 c 0.17994,-1.312828 1.92575,-9.548915 2.26206,-10.671614 l 0.19815,-0.661459 10.14805,-0.06934 c 5.58143,-0.03813 10.25133,0.03394 10.37756,0.160167 0.2195,0.219503 -0.14839,2.336307 -1.38672,7.978962 -0.30339,1.382448 -0.6413,2.93026 -0.7509,3.439583 l -0.19928,0.926046 -10.4095,0.0692 -10.4095,0.0693 z" />
                  </g>
                </svg>
                Epic
              </button>
            )}

            {visibleProviders.webex && (
              <button
                type="button"
                onClick={() => handleOAuthClick('webex')}
                className="min-w-[80px] flex-1 inline-flex flex-col justify-center items-center py-4 px-2 border border-gray-300 rounded-md shadow-sm bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img src="/webex.png" alt="Webex" className="w-6 h-6 mb-1.5" />
                Webex
              </button>
            )}

            {visibleProviders.zoom && (
              <button
                type="button"
                onClick={() => handleOAuthClick('zoom')}
                className={`min-w-[80px] flex-1 inline-flex flex-col justify-center items-center py-4 px-2 border border-gray-300 rounded-md shadow-sm bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed${inZoomClient ? ' w-full' : ''}`}
              >
                <img src="/zoom.png" alt="Zoom" className="w-6 h-6 mb-1.5" />
                Zoom
              </button>
            )}
          </div>
        )}

        {mode !== "admin" && !hasOAuthOptions && (
          <div className="text-center text-gray-600">
            No login options available for this organization.
          </div>
        )}
      </div>
    </>
  );
};

export default Login;
